import type { SDKLocale } from './index'

/**
 * Open API business error codes (46xxx) mapped to user-facing messages.
 * Source: Open API v1 error code tables. Codes not listed here fall back to the
 * backend `message` when present, otherwise a generic retry message for Open
 * API endpoints (see `errors.requestRetry`).
 */
type LocalizedMessage = Record<SDKLocale, string>

/** 46006–46008 are internal capacity/permission failures; surfaced as a single overload hint. */
const SYSTEM_OVERLOADED: LocalizedMessage = {
  'zh-CN': '当前使用人数过多，请稍后再试',
  'en-US': 'The system is currently overloaded. Please retry after some time.',
}

/** 46009–46010: server connection failures. */
const CONNECTION_FAILED: LocalizedMessage = {
  'zh-CN': '连接服务器失败，请稍后重试',
  'en-US': 'Failed to connect to the server. Please try again later.',
}

export const API_ERROR_MESSAGES: Record<string, LocalizedMessage> = {
  '46001': { 'zh-CN': 'X-Api-Key 不能为空', 'en-US': 'X-Api-Key cannot be empty' },
  '46002': { 'zh-CN': '无效的用户 API Key', 'en-US': 'Invalid user API Key' },
  '46003': { 'zh-CN': 'model 不能为空', 'en-US': 'model cannot be empty' },
  '46004': { 'zh-CN': 'sessionUid 不能为空', 'en-US': 'sessionUid cannot be empty' },
  '46005': { 'zh-CN': '模型类型不存在', 'en-US': 'Model type does not exist' },
  '46006': SYSTEM_OVERLOADED,
  '46007': SYSTEM_OVERLOADED,
  '46008': SYSTEM_OVERLOADED,
  '46009': CONNECTION_FAILED,
  '46010': CONNECTION_FAILED,
  '46012': { 'zh-CN': '会话不存在', 'en-US': 'Session does not exist' },
  '46013': { 'zh-CN': '会话不属于当前用户', 'en-US': 'Session does not belong to the current user' },
  '46014': { 'zh-CN': '会话已关闭', 'en-US': 'Session has been closed' },
  '46015': { 'zh-CN': '会话已超时', 'en-US': 'Session has timed out' },
  '46018': { 'zh-CN': '临时 API Key 已过期', 'en-US': 'Temporary API Key has expired' },
  '46019': {
    'zh-CN': '临时 API Key 已达到积分上限',
    'en-US': 'Temporary API Key has reached its points limit',
  },
  '46021': {
    'zh-CN': '临时 API Key 不允许再创建临时 API Key',
    'en-US': 'A temporary API Key cannot create another temporary API Key',
  },
  '40030': { 'zh-CN': '请求体不能为空', 'en-US': 'Request body cannot be empty' },
  '40032': { 'zh-CN': 'refImagePath 不是有效的 URL', 'en-US': 'refImagePath is not a valid URL' },
  '40033': { 'zh-CN': 'refImagePath 不是图片 URL', 'en-US': 'refImagePath is not an image URL' },
  '40034': { 'zh-CN': 'refVideoPath 不能为空', 'en-US': 'refVideoPath cannot be empty' },
  '40035': { 'zh-CN': 'refVideoPath 不是有效的 URL', 'en-US': 'refVideoPath is not a valid URL' },
  '40036': { 'zh-CN': 'refVideoPath 不是视频 URL', 'en-US': 'refVideoPath is not a video URL' },
  '40037': { 'zh-CN': '无法读取源视频信息', 'en-US': 'Unable to read source video metadata' },
  '40038': { 'zh-CN': 'prompt 不能为空', 'en-US': 'prompt cannot be empty' },
  '40039': { 'zh-CN': 'prompt 不能超过 1024 个字符', 'en-US': 'prompt cannot exceed 1024 characters' },
  '40040': { 'zh-CN': 'status 不是支持的任务状态', 'en-US': 'status is not a supported task status' },
  '40041': { 'zh-CN': 'pageSize 不能超过 100', 'en-US': 'pageSize cannot exceed 100' },
  '40043': { 'zh-CN': '源视频时长不能少于 1 秒', 'en-US': 'Source video must be at least 1 second long' },
  '40044': { 'zh-CN': 'fps 不能低于 8', 'en-US': 'fps cannot be lower than 8' },
  '40045': { 'zh-CN': '正在等待或处理的任务已达到 100 个', 'en-US': 'The active offline task limit of 100 has been reached' },
  '40046': { 'zh-CN': 'taskUids 不能为空', 'en-US': 'taskUids cannot be empty' },
  '40047': { 'zh-CN': 'taskUids 不能超过 100 个', 'en-US': 'taskUids cannot contain more than 100 IDs' },
  '46017': { 'zh-CN': '积分不足', 'en-US': 'Insufficient credits' },
  '46023': { 'zh-CN': 'taskUid 不能为空', 'en-US': 'taskUid cannot be empty' },
  '46024': { 'zh-CN': '离线任务不存在', 'en-US': 'Offline task does not exist' },
  '46025': { 'zh-CN': '离线任务不属于当前用户', 'en-US': 'Offline task does not belong to the current user' },
  '46026': { 'zh-CN': 'quality 必须是 sd 或 hd', 'en-US': 'quality must be sd or hd' },
  '46027': { 'zh-CN': 'fps 不是支持的值', 'en-US': 'fps is not supported' },

}

/** Resolve a business error code to a localized message, or `undefined` if unmapped. */
export function resolveApiErrorMessage(
  code: string | number | null | undefined,
  locale: SDKLocale,
): string | undefined {
  if (code === null || code === undefined) return undefined
  const entry = API_ERROR_MESSAGES[String(code)]
  if (!entry) return undefined
  return entry[locale] ?? entry['en-US']
}
